import { Drawer } from "@mantine/core";
import {
    CaregiverDrawer,
    EditTableViewModal,
    EmbeddedCodeModal,
    FilePreviewModal,
    GlobalInviteModal,
    HelpRequestModal,
    LocationDrawer,
    ManageProvidersForPatientModal,
    OrganizationDrawer,
    OverlayZIndexProvider,
    PatientDrawer,
    PriorityEntryModal,
    PriorityPublicInviteModal,
    PriorityReferralModal,
    PriorityStatusSimpleEmailModal,
    SettingsDrawer,
    TeamDrawer,
    TherapproveOverlaysContextProvider,
    UploadFileModal,
    useTherapproveDrawersState,
    useTherapproveModalsState
} from "@therapprove/common";
import type { PropsWithChildren } from "react";

export const TherapproveOverlayProvider = ({
    children
}: PropsWithChildren) => {
    const modals = useTherapproveModalsState();
    const drawers = useTherapproveDrawersState();
    const handleLocationClose = () => {
        drawers.location.close();
        drawers.location.props?.afterClose?.();
    };

    return (
        <OverlayZIndexProvider>
        <TherapproveOverlaysContextProvider
            value={{ modals, drawers }}
        >
            {children}

            {/* ── Modals ── */}
            <UploadFileModal
                fileInfo={modals.fileUpload.props?.fileInfo}
                initialFiles={modals.fileUpload.props?.initialFiles}
                opened={modals.fileUpload.opened}
                close={modals.fileUpload.close}
            />
            <HelpRequestModal
                opened={modals.helpRequest.opened}
                close={modals.helpRequest.close}
            />
            <PriorityPublicInviteModal
                opened={modals.priorityPublicInvite.opened}
                close={modals.priorityPublicInvite.close}
            />
            {modals.editPriorityView.props && (
                <EditTableViewModal
                    {...modals.editPriorityView.props}
                    opened={modals.editPriorityView.opened}
                    close={modals.editPriorityView.close}
                />
            )}
            {modals.embeddedCode.props && (
                <EmbeddedCodeModal
                    {...modals.embeddedCode.props}
                    opened={modals.embeddedCode.opened}
                    close={modals.embeddedCode.close}
                />
            )}
            {modals.priorityReferral.props && (
                <PriorityReferralModal
                    {...modals.priorityReferral.props}
                    opened={modals.priorityReferral.opened}
                    close={modals.priorityReferral.close}
                />
            )}
            {modals.priorityStatusSimpleEmail.props && (
                <PriorityStatusSimpleEmailModal
                    {...modals.priorityStatusSimpleEmail.props}
                    opened={modals.priorityStatusSimpleEmail.opened}
                    close={modals.priorityStatusSimpleEmail.close}
                />
            )}
            {modals.filePreview.props && (
                <FilePreviewModal
                    {...modals.filePreview.props}
                    opened={modals.filePreview.opened}
                    close={modals.filePreview.close}
                />
            )}
            <ManageProvidersForPatientModal
                patientId={modals.manageProvidersForPatient.props?.patientId}
                opened={modals.manageProvidersForPatient.opened}
                close={modals.manageProvidersForPatient.close}
            />
            {modals.globalInvite.props && (
                <GlobalInviteModal
                    {...modals.globalInvite.props}
                    opened={modals.globalInvite.opened}
                    close={modals.globalInvite.close}
                />
            )}

            {/* ── Drawers ── */}
            <Drawer.Stack>
            <OrganizationDrawer
                organizationId={drawers.organization.props?.organizationId}
                options={drawers.organization.props?.options ?? {}}
                opened={drawers.organization.opened}
                close={drawers.organization.close}
            />
            <CaregiverDrawer
                caregiverId={drawers.caregiver.props?.caregiverId}
                opened={drawers.caregiver.opened}
                close={drawers.caregiver.close}
            />
            <PatientDrawer
                patientId={drawers.patient.props?.patientId}
                caregiverId={drawers.patient.props?.caregiverId}
                opened={drawers.patient.opened}
                close={drawers.patient.close}
            />
            <PriorityEntryModal
                {...drawers.priorityEntry.props}
                opened={drawers.priorityEntry.opened}
                close={drawers.priorityEntry.close}
            />
            <LocationDrawer
                organizationId={drawers.location.props?.organizationId}
                locationId={drawers.location.props?.locationId}
                options={drawers.location.props?.options}
                opened={drawers.location.opened}
                close={handleLocationClose}
            />
            <SettingsDrawer
                opened={drawers.settings.opened}
                close={drawers.settings.close}
            />
            <TeamDrawer
                userId={drawers.team.props?.userId}
                opened={drawers.team.opened}
                close={drawers.team.close}
            />
            </Drawer.Stack>
        </TherapproveOverlaysContextProvider>
        </OverlayZIndexProvider>
    );
};
